import React, { useState } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import {
  Container, HeaderRow, Title, HeaderMeta, Badge, DividerDot, Small, PageContent, Grid, Card, CardHeader, CardTitle,
  Note, Mono, List, Item, Key, Desc, EndpointRow, EndpointLabel, EndpointValue, CodeBlock, CodeArea, Toolbar, LangPill,
  Spacer, CopyBtn, Tabs, Tab, ErrorList, ErrorRow, ErrCode, ErrText, WorkflowSteps, Step, StepNumber, StepContent, StepTitle,
  StepDesc, FlowDiagram, FlowStep, FlowLabel, FlowArrow,
} from "../../Components/Documentation/DocumentationStyles";
import { Copy } from "lucide-react";
import useCopyWithFeedback from "../../hooks/useCopyWithFeedback";

const AsyncTranslateDocumentation = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || "";
  const [activeTab, setActiveTab] = useState("flow");
  const { copied, handleCopy } = useCopyWithFeedback();

  const endpoint = `${API_BASE_URL.replace(/\/?$/, "/")}api/v1/connector/integration/translate_async`;

  const createResponse = `{
  "job_id": "3f1c…",
  "status": "awaiting_upload",
  "upload_url": "https://s3…",
  "expires_in": 7200,
  "from": "en-GB",
  "languages": ["ro-RO", "ja-JP"],
  "created_at": "2026-08-10T09:00:00Z"
}`;

  const statusResponse = `{
  "job_id": "3f1c…",
  "status": "processing",
  "from": "en-GB",
  "languages": [
    {
      "language": "ro-RO",
      "status": "completed",
      "complete": true,
      "download_url": "https://s3…",
      "download_url_expires_in": 14400
    },
    { "language": "ja-JP", "status": "processing", "complete": false }
  ]
}`;

  const curlExample = `# Step 1: Create the job
curl --location '${endpoint}' \\
  --header 'X-API-KEY: YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "from": "en-GB",
    "languages": ["ro-RO", "ja-JP"]
  }'

# Step 2: Upload the document straight to S3
curl --request PUT \\
  --data-binary '@/path/to/document.txt' \\
  'UPLOAD_URL_FROM_STEP_1'

# Step 3: Start the job
curl --location '${endpoint}/JOB_ID/start' \\
  --request PUT \\
  --header 'X-API-KEY: YOUR_API_KEY'

# Step 4: Poll for status
curl --location '${endpoint}/JOB_ID' \\
  --header 'X-API-KEY: YOUR_API_KEY'

# Step 5: Cancel (optional)
curl --location '${endpoint}/JOB_ID' \\
  --request DELETE \\
  --header 'X-API-KEY: YOUR_API_KEY'`;

  return (
    <Container>
      <HeaderRow>
        <Title data-emotii-exclude>Async Text Translation API</Title>
        <HeaderMeta>
          <Badge tone="brand" data-emotii-exclude>v1</Badge>
          <DividerDot />
          <Small>Async • S3 Upload</Small>
        </HeaderMeta>
      </HeaderRow>

      <PageContent>
        <Grid>
          <Card $span2>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <Note>
              This API uses an <b>asynchronous workflow</b> to translate whole documents without timeout issues.
              The document is uploaded once and translated into up to 10 target languages, each handled by its
              own worker. The flow is: <b>create → upload → start → poll → download</b>.
            </Note>
            <Note style={{ marginTop: "12px" }}>
              <b>Languages finish independently.</b> Each target language can be downloaded as soon as it is
              ready, without waiting for the rest of the job.
            </Note>
            <Note style={{ marginTop: "12px" }}>
              <b>Supported formats:</b> <Mono data-emotii-exclude>.txt</Mono>
            </Note>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Workflow Steps</CardTitle>
            </CardHeader>
            <WorkflowSteps>
              <Step>
                <StepNumber>1</StepNumber>
                <StepContent>
                  <StepTitle>Create the job</StepTitle>
                  <StepDesc>
                    <Mono data-emotii-exclude>POST /translate_async</Mono> - reserves the job and hands back
                    somewhere to put the file
                  </StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>2</StepNumber>
                <StepContent>
                  <StepTitle>Upload the document</StepTitle>
                  <StepDesc>
                    <Mono data-emotii-exclude>PUT</Mono> the raw document to the{" "}
                    <Mono data-emotii-exclude>upload_url</Mono> - straight to S3, not through our API
                  </StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>3</StepNumber>
                <StepContent>
                  <StepTitle>Start the job</StepTitle>
                  <StepDesc>
                    <Mono data-emotii-exclude>PUT /translate_async/:job_id/start</Mono> - queues one worker per
                    target language
                  </StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>4</StepNumber>
                <StepContent>
                  <StepTitle>Poll for status</StepTitle>
                  <StepDesc>
                    <Mono data-emotii-exclude>GET /translate_async/:job_id</Mono> - metadata only, never document
                    text
                  </StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>5</StepNumber>
                <StepContent>
                  <StepTitle>Cancel (optional)</StepTitle>
                  <StepDesc>
                    <Mono data-emotii-exclude>DELETE /translate_async/:job_id</Mono> - partial results are kept
                  </StepDesc>
                </StepContent>
              </Step>
            </WorkflowSteps>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Step 1: Create the job</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>POST</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}</EndpointValue>
            </EndpointRow>
            <Note style={{ marginTop: "12px" }}>
              <b>Headers:</b> <Mono data-emotii-exclude>X-API-KEY</Mono>,{" "}
              <Mono data-emotii-exclude>Content-Type: application/json</Mono>
            </Note>
            <Note>
              No document in the body. This only reserves the job and hands back somewhere to put the file.
            </Note>

            <SubTitle>Body</SubTitle>
            <List>
              <Item>
                <Key data-emotii-exclude>from</Key>
                <Desc>
                  <RequiredBadge>Required</RequiredBadge> Source locale, e.g.{" "}
                  <Mono data-emotii-exclude>en-GB</Mono>
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>languages</Key>
                <Desc>
                  <RequiredBadge>Required</RequiredBadge> Array of target locales. De-duplicated. 1–10 entries.
                  Cannot contain <Mono data-emotii-exclude>from</Mono>
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>mood</Key>
                <Desc>
                  <OptionalBadge>Optional</OptionalBadge> e.g. <Mono data-emotii-exclude>formal</Mono> - same
                  personalisation fields as the sync endpoint
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>cultural_background</Key>
                <Desc>
                  <OptionalBadge>Optional</OptionalBadge>
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>gender</Key>
                <Desc>
                  <OptionalBadge>Optional</OptionalBadge>
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>born_in</Key>
                <Desc>
                  <OptionalBadge>Optional</OptionalBadge>
                </Desc>
              </Item>
            </List>

            <Note style={{ marginTop: "16px" }}>
              Locales must come from <Mono data-emotii-exclude>TRANSLATION_LANGUAGES</Mono> (
              <Mono data-emotii-exclude>en-GB</Mono>, <Mono data-emotii-exclude>ro-RO</Mono>,{" "}
              <Mono data-emotii-exclude>ja-JP</Mono>, …). Names like <Mono data-emotii-exclude>English</Mono> are
              not accepted.
            </Note>

            <SubTitle>
              Returns <StatusPill data-emotii-exclude>202</StatusPill>
            </SubTitle>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{createResponse}</code>
              </CodeArea>
            </CodeBlock>

            <Note style={{ marginTop: "16px" }}>
              <Mono data-emotii-exclude>upload_url</Mono> is a presigned S3 PUT, valid <b>2 hours</b>. Nothing is
              queued and nothing is billed at this point.
            </Note>

            <SubTitle>Errors</SubTitle>
            <ErrorList>
              <ErrorRow>
                <ErrCode data-emotii-exclude>422</ErrCode>
                <ErrText>
                  Unsupported locale, empty <Mono data-emotii-exclude>languages</Mono>, more than 10, or a target
                  equal to <Mono data-emotii-exclude>from</Mono>.
                </ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode data-emotii-exclude>429</ErrCode>
                <ErrText>The project already has 5 unfinished jobs.</ErrText>
              </ErrorRow>
            </ErrorList>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Step 2: Upload the document</CardTitle>
            </CardHeader>
            <Note>
              <Mono data-emotii-exclude>PUT</Mono> the raw document to the{" "}
              <Mono data-emotii-exclude>upload_url</Mono> from step 1. Straight to S3, not through our API. Max{" "}
              <b>50 MB</b>.
            </Note>
            <Note>
              If the job is never started, both the row and the uploaded file are swept after 2 hours.
            </Note>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Step 3: Start the job</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>PUT</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}/:job_id/start</EndpointValue>
            </EndpointRow>
            <Note style={{ marginTop: "12px" }}>
              <b>Headers:</b> <Mono data-emotii-exclude>X-API-KEY</Mono>
            </Note>
            <Note>
              No body. Checks the file is actually in S3, then queues one worker per target language.
            </Note>
            <Note>
              <b>Returns</b> <StatusPill data-emotii-exclude>200</StatusPill> with the same body as Job Status
              (below).
            </Note>

            <SubTitle>Errors</SubTitle>
            <ErrorList>
              <ErrorRow>
                <ErrCode data-emotii-exclude>422</ErrCode>
                <ErrText>Nothing was uploaded.</ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode data-emotii-exclude>413</ErrCode>
                <ErrText>File over 50 MB (checked without downloading it).</ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode data-emotii-exclude>404</ErrCode>
                <ErrText>Unknown job, or a job belonging to another project.</ErrText>
              </ErrorRow>
            </ErrorList>

            <Note style={{ marginTop: "16px" }}>
              <b>Idempotent.</b> Calling it twice returns <Mono data-emotii-exclude>200</Mono> both times and
              queues one set of workers, not two. Safe to retry after a timeout.
            </Note>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Step 4: Poll for status</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>GET</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}/:job_id</EndpointValue>
            </EndpointRow>
            <Note style={{ marginTop: "12px" }}>
              <b>Headers:</b> <Mono data-emotii-exclude>X-API-KEY</Mono>
            </Note>
            <Note>
              Returns metadata only, never document text, so polling stays cheap no matter how large the result.
            </Note>
            <Note>
              <b>Query params</b> - <Mono data-emotii-exclude>language=ro-RO</Mono> restricts the response to a
              single language.
            </Note>

            <SubTitle>
              Returns <StatusPill data-emotii-exclude>200</StatusPill>
            </SubTitle>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{statusResponse}</code>
              </CodeArea>
            </CodeBlock>

            <SubTitle>Per-language fields</SubTitle>
            <List>
              <Item>
                <Key data-emotii-exclude>status</Key>
                <Desc>
                  <Mono data-emotii-exclude>pending</Mono>, <Mono data-emotii-exclude>processing</Mono>,{" "}
                  <Mono data-emotii-exclude>completed</Mono>, <Mono data-emotii-exclude>failed</Mono>,{" "}
                  <Mono data-emotii-exclude>quota_exceeded</Mono>, <Mono data-emotii-exclude>canceled</Mono>
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>complete</Key>
                <Desc>
                  <Mono data-emotii-exclude>true</Mono> only when the whole document was translated
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>download_url</Key>
                <Desc>
                  Presigned GET, valid <b>4 hours</b>. Present whenever that language has a result, including a
                  partial one
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>error_message</Key>
                <Desc>Present only on failure</Desc>
              </Item>
            </List>

            <Note style={{ marginTop: "16px" }}>
              <b>Job-level</b> <Mono data-emotii-exclude>status</Mono> -{" "}
              <Mono data-emotii-exclude>awaiting_upload</Mono>, <Mono data-emotii-exclude>pending</Mono>,{" "}
              <Mono data-emotii-exclude>processing</Mono>, <Mono data-emotii-exclude>completed</Mono>,{" "}
              <Mono data-emotii-exclude>partial</Mono>, <Mono data-emotii-exclude>failed</Mono>,{" "}
              <Mono data-emotii-exclude>quota_exceeded</Mono>, <Mono data-emotii-exclude>canceled</Mono>
            </Note>

            <Callout>
              Read <Mono data-emotii-exclude>complete</Mono> before using a download. It is{" "}
              <Mono data-emotii-exclude>false</Mono> when the job ran out of quota, was cancelled, or failed
              part-way. The finished portion is still served - it has already been billed - but it is not the
              whole document. A client that ignores this field will happily save a book missing its last third.
            </Callout>

            <Note>
              Languages finish independently, so Romanian can be downloaded while Japanese is still running.
            </Note>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Step 5: Cancel (optional)</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>DELETE</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}/:job_id</EndpointValue>
            </EndpointRow>
            <Note style={{ marginTop: "12px" }}>
              <b>Headers:</b> <Mono data-emotii-exclude>X-API-KEY</Mono>
            </Note>
            <Note>
              <b>Returns</b> <StatusPill data-emotii-exclude>200</StatusPill> with the same body as Job Status.
            </Note>
            <Note>
              Cancelling an already finished job is a no-op and still returns{" "}
              <Mono data-emotii-exclude>200</Mono>. Any language that had already translated part of the document
              keeps its result and <Mono data-emotii-exclude>download_url</Mono> with{" "}
              <Mono data-emotii-exclude>complete: false</Mono>.
            </Note>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Complete Example</CardTitle>
              <Tabs>
                <Tab aria-selected={activeTab === "flow"} onClick={() => setActiveTab("flow")}>
                  Flow Diagram
                </Tab>
                <Tab aria-selected={activeTab === "curl"} onClick={() => setActiveTab("curl")}>
                  cURL
                </Tab>
              </Tabs>
            </CardHeader>

            {activeTab === "flow" && (
              <FlowDiagram>
                <FlowStep>
                  <FlowLabel>Client</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>POST /translate_async</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Get job_id + upload_url</FlowLabel>
                </FlowStep>
                <FlowStep>
                  <FlowLabel>Client</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>PUT to S3 URL</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Upload complete</FlowLabel>
                </FlowStep>
                <FlowStep>
                  <FlowLabel>Client</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>PUT /:job_id/start</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>One worker per target language</FlowLabel>
                </FlowStep>
                <FlowStep>
                  <FlowLabel>Server</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Translate each language</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Upload results to S3</FlowLabel>
                </FlowStep>
                <FlowStep>
                  <FlowLabel>Client</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>GET /:job_id (poll)</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Get download_url per language</FlowLabel>
                </FlowStep>
              </FlowDiagram>
            )}

            {activeTab === "curl" && (
              <CodeBlock>
                <Toolbar>
                  <LangPill data-emotii-exclude>bash</LangPill>
                  <Spacer />
                  <CopyBtn
                    onClick={() => handleCopy("curl", curlExample)}
                    data-copied={copied === "curl" ? "true" : "false"}
                  >
                    <Copy size={14} />
                    {copied === "curl" ? "Copied" : "Copy"}
                  </CopyBtn>
                </Toolbar>
                <CodeArea role="region" aria-label="curl Example" data-emotii-exclude>
                  <code>{curlExample}</code>
                </CodeArea>
              </CodeBlock>
            )}
          </Card>
        </Grid>
      </PageContent>
    </Container>
  );
};

export default AsyncTranslateDocumentation;

const SubTitle = styled.div`
  color: ${lightThemeColors.darkColor};
  font-size: 13px;
  font-weight: 800;
  margin: 16px 0 10px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusPill = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.3px;
  color: #059669;
  background: #d1fae5;
  border: 1px solid #a7f3d0;
  padding: 3px 8px;
  border-radius: 6px;
`;

const Pill = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
  padding: 2px 9px;
  border-radius: 999px;
  margin-right: 8px;
  white-space: nowrap;
  vertical-align: 1px;
`;

const RequiredBadge = styled(Pill)`
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fde68a;
`;

const OptionalBadge = styled(Pill)`
  color: #059669;
  background: #d1fae5;
  border: 1px solid #a7f3d0;
`;

const Callout = styled.p`
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.6;
  margin: 16px 0 14px;
`;